import React, { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  TrendingUp,
  Users,
  BookOpen,
  Award,
  Target,
  Filter,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getAnalyticsDashboard,
  getStudentProgressAnalytics,
  getCourseCompletionAnalytics,
  getQuizPerformanceAnalytics,
  StudentProgressData,
  CourseCompletionData,
  QuizPerformanceData,
} from "../../services/analytics";

const TeacherAnalytics: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [activeTab, setActiveTab] = useState<
    "overview" | "students" | "courses" | "quizzes"
  >("overview");
  const [filters, setFilters] = useState({
    courseId: "",
    startDate: "",
    endDate: "",
  });

  // Dashboard overview query
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ["analytics-dashboard"],
    queryFn: getAnalyticsDashboard,
    enabled: !!user?.id,
  });

  // Student progress query
  const { data: studentProgressData, isLoading: studentProgressLoading } =
    useQuery({
      queryKey: ["student-progress-analytics", filters],
      queryFn: () =>
        getStudentProgressAnalytics({
          courseId: filters.courseId || undefined,
        }),
      enabled: !!user?.id && activeTab === "students",
    });

  // Course completion query
  const { data: courseCompletionData, isLoading: courseCompletionLoading } =
    useQuery({
      queryKey: ["course-completion-analytics", filters],
      queryFn: () =>
        getCourseCompletionAnalytics({
          courseId: filters.courseId || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        }),
      enabled: !!user?.id && activeTab === "courses",
    });

  // Quiz performance query
  const { data: quizPerformanceData, isLoading: quizPerformanceLoading } =
    useQuery({
      queryKey: ["quiz-performance-analytics", filters],
      queryFn: () =>
        getQuizPerformanceAnalytics({
          courseId: filters.courseId || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        }),
      enabled: !!user?.id && activeTab === "quizzes",
    });

  if (dashboardLoading) {
    return <LoadingSpinner />;
  }

  const overview = dashboardData?.data?.overview;

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    trend?: string;
  }> = ({ title, value, icon, color, trend }) => (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${color}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {trend && (
            <p className="text-sm text-green-600 mt-1 flex items-center">
              <TrendingUp className="w-4 h-4 mr-1" />
              {trend}
            </p>
          )}
        </div>
        <div className="text-gray-400">{icon}</div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          E-Learning Analytics
        </h1>
        <p className="text-gray-600 mt-1">
          Monitor student progress, course performance, and quiz results
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Course ID (Optional)
            </label>
            <input
              type="text"
              value={filters.courseId}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, courseId: e.target.value }))
              }
              placeholder="Enter course ID to filter"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, startDate: e.target.value }))
              }
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, endDate: e.target.value }))
              }
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "students", label: "Student Progress", icon: Users },
            { id: "courses", label: "Course Completion", icon: BookOpen },
            { id: "quizzes", label: "Quiz Performance", icon: Award },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            title="Total Students"
            value={overview.totalStudents}
            icon={<Users className="w-8 h-8" />}
            color="border-l-4 border-l-blue-500"
          />
          <StatCard
            title="Total Courses"
            value={overview.totalCourses}
            icon={<BookOpen className="w-8 h-8" />}
            color="border-l-4 border-l-green-500"
          />
          <StatCard
            title="Total Enrollments"
            value={overview.totalEnrollments}
            icon={<Target className="w-8 h-8" />}
            color="border-l-4 border-l-purple-500"
          />
          <StatCard
            title="Total Quizzes"
            value={overview.totalQuizzes}
            icon={<Award className="w-8 h-8" />}
            color="border-l-4 border-l-orange-500"
          />
          <StatCard
            title="Completion Rate"
            value={`${overview.completionRate}%`}
            icon={<TrendingUp className="w-8 h-8" />}
            color="border-l-4 border-l-indigo-500"
            trend="Average completion"
          />
          <StatCard
            title="Quiz Pass Rate"
            value={`${overview.quizPassRate}%`}
            icon={<Award className="w-8 h-8" />}
            color="border-l-4 border-l-red-500"
            trend="Average pass rate"
          />
        </div>
      )}

      {activeTab === "students" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Student Progress Analytics
            </h2>
            <p className="text-gray-600 mt-1">
              Detailed progress tracking for individual students
            </p>
          </div>
          {studentProgressLoading ? (
            <div className="p-6">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Course
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modules
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time Spent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Access
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {studentProgressData?.data?.map(
                    (student: StudentProgressData) => (
                      <tr key={`${student.studentId}-${student.courseId}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {student.studentName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.courseName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${student.overallProgress}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600">
                              {student.overallProgress}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.modulesCompleted}/{student.totalModules}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {Math.round(student.timeSpent)} min
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.lastAccessedAt
                            ? new Date(
                                student.lastAccessedAt
                              ).toLocaleDateString()
                            : "Never"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "courses" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Course Completion Analytics
            </h2>
            <p className="text-gray-600 mt-1">
              Completion rates and performance metrics for courses
            </p>
          </div>
          {courseCompletionLoading ? (
            <div className="p-6">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Course
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Enrollments
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completion Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg. Completion
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {courseCompletionData?.data?.map(
                    (course: CourseCompletionData) => (
                      <tr key={course.courseId}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {course.courseName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {course.totalEnrollments}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {course.completedEnrollments}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{ width: `${course.completionRate}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600">
                              {course.completionRate}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {course.averageCompletion}%
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "quizzes" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Quiz Performance Analytics
            </h2>
            <p className="text-gray-600 mt-1">
              Detailed quiz performance metrics and student results
            </p>
          </div>
          {quizPerformanceLoading ? (
            <div className="p-6">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quiz
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attempts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pass Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Highest/Lowest
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Time
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quizPerformanceData?.data?.map(
                    (quiz: QuizPerformanceData) => (
                      <tr key={quiz.quizId}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {quiz.quizTitle}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {quiz.totalAttempts}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {quiz.averageScore}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${quiz.passRate}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600">
                              {quiz.passRate}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {quiz.highestScore}% / {quiz.lowestScore}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {Math.round(quiz.averageTimeSpent)} min
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeacherAnalytics;
